import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Banknote, CalendarCheck, ChevronLeft, ChevronRight, Clock, Copy, Download, Eye, Flag, HandCoins, Images, MapPin, MessageCircle, Phone, Scale, Send, Share2, ShieldAlert, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate, fmtPrice, media, GENDER_LABEL, METHOD_LABEL, SERVICE_LABEL } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useVisitor } from "@/lib/visitor";
import { AvailableBadge, DemoTag, VerifiedBadge } from "@/components/lux/Badges";
import { ReportDialog } from "@/components/lux/ReportForm";
import StoriesRail from "@/components/lux/StoriesRail";
import SaveButton from "@/components/lux/SaveButton";
import { WhatsAppIcon } from "@/components/lux/Icons";

const METHOD_ICON = { phone: Phone, whatsapp: WhatsAppIcon, telegram: Send, signal: MessageCircle };

function Gallery({ photos, name, demo }) {
  const [i, setI] = useState(0);
  const { blur } = useVisitor();
  const [revealed, setRevealed] = useState(false);
  useEffect(() => setRevealed(false), [blur]);
  if (!photos?.length) return null;
  const hidden = blur && !revealed;
  const go = (d) => setI((i + d + photos.length) % photos.length);
  return (
    <div data-testid="profile-gallery" onKeyDown={(e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); }}>
      <div className="relative overflow-hidden rounded-[12px] border border-lux-border bg-lux-surface2">
        <div className="aspect-[4/5] sm:aspect-[3/4]">
          {photos.map((p, idx) => (
            <img key={p.id} src={media(p.url)} alt={`${demo ? "Demo profile photo" : "Photo"} ${idx + 1} of ${name}`} loading={idx === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${idx === i ? "opacity-100" : "opacity-0"} ${hidden ? "blur-2xl" : ""}`} />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10" />
        {hidden && (
          <button onClick={() => setRevealed(true)} className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-lux-text" data-testid="profile-gallery-reveal">
            <span className="inline-flex items-center gap-2 rounded-full border border-lux-border bg-black/60 px-4 py-2 text-sm backdrop-blur"><Eye className="h-4 w-4" /> Reveal photos</span>
            <span className="text-xs text-lux-muted">Photos are blurred while discreet browsing is on</span>
          </button>
        )}
        {photos.length > 1 && !hidden && (
          <>
            <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-lux-border bg-black/55 p-2.5 text-lux-silver backdrop-blur hover:text-lux-silverBright" aria-label="Previous photo" data-testid="profile-gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-lux-border bg-black/55 p-2.5 text-lux-silver backdrop-blur hover:text-lux-silverBright" aria-label="Next photo" data-testid="profile-gallery-next"><ChevronRight className="h-5 w-5" /></button>
            <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5" aria-hidden="true">
              {photos.map((p, idx) => <span key={p.id} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-lux-silverBright" : "w-1.5 bg-white/40"}`} />)}
            </div>
          </>
        )}
        <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-lux-silver" aria-live="polite">{i + 1} / {photos.length}</span>
      </div>
      {photos.length > 1 && (
        <div className="lux-scroll-x mt-3 flex gap-2 overflow-x-auto">
          {photos.map((p, idx) => (
            <button key={p.id} onClick={() => setI(idx)} className={`h-20 w-16 shrink-0 overflow-hidden rounded-[8px] border transition-colors ${idx === i ? "border-lux-silverBright" : "border-lux-border opacity-70 hover:opacity-100"}`}
              aria-label={`Show photo ${idx + 1}`} data-testid={`profile-gallery-thumb-${idx}`}>
              <img src={media(p.thumb)} alt="" className={`h-full w-full object-cover ${hidden ? "blur-md" : ""}`} loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const isMobile = () => window.matchMedia && window.matchMedia("(max-width: 768px)").matches;

function openHref(href) {
  if (href.startsWith("http") && !isMobile()) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
}

/** Safe-contact step before WhatsApp opens: a short reminder + the respectful pre-written first message. */
function WhatsAppDialog({ open, onOpenChange, slug, name, intro, onOpened }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [link, setLink] = useState(null);
  const go = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/public/profiles/${slug}/contact`, { method: "whatsapp" });
      setLink(data);
      onOpened?.(data);
      openHref(data.href);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.display);
      toast.success("Number copied");
    } catch (e) {
      toast.error("Couldn't copy the number");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-lux-border bg-lux-surface text-lux-text sm:max-w-md" data-testid="whatsapp-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-medium"><WhatsAppIcon className="h-5 w-5 text-lux-silver" /> Message {name} on WhatsApp</DialogTitle>
          <DialogDescription className="text-lux-muted">WhatsApp opens on your device. LUX has no in-app messaging and never sees your conversation.</DialogDescription>
        </DialogHeader>
        <div className="rounded-[10px] border border-lux-border bg-lux-surface2 p-4" data-testid="whatsapp-intro-preview">
          <p className="lux-label">Pre-written first message</p>
          <p className="mt-2 font-serif text-lg italic leading-snug text-lux-silverBright">“{intro}”</p>
          <p className="mt-2 text-xs text-lux-muted">You can edit it before sending. Advertisers appreciate polite, clear introductions.</p>
        </div>
        <ul className="space-y-2 text-[13px] text-lux-muted">
          <li className="flex gap-2"><HandCoins className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" /> Never pay a deposit. LUX never takes payments, so any request to pay “through LUX” is a scam.</li>
          <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" /> If the person replying doesn’t seem to be the advertiser, stop and report the profile.</li>
        </ul>
        {err && <p className="text-sm text-lux-error" role="alert" data-testid="whatsapp-dialog-error">{err}</p>}
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {!link ? (
            <button onClick={go} disabled={busy} className="lux-btn lux-btn-primary w-full" data-testid="whatsapp-dialog-open-button"><WhatsAppIcon className="h-4 w-4" /> {busy ? "Opening..." : "Open WhatsApp"}</button>
          ) : (
            <>
              <a href={link.href} target={isMobile() ? undefined : "_blank"} rel="noopener noreferrer nofollow" className="lux-btn lux-btn-primary w-full" data-testid="whatsapp-dialog-revealed-link"><WhatsAppIcon className="h-4 w-4" /> WhatsApp: {link.display}</a>
              <button onClick={copy} className="lux-btn lux-btn-secondary w-full" data-testid="whatsapp-dialog-copy-button"><Copy className="h-4 w-4" /> Copy number</button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecondaryContact({ slug, method }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const Icon = METHOD_ICON[method] || Phone;
  const reveal = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/public/profiles/${slug}/contact`, { method });
      setState(data);
      if (isMobile()) window.location.href = data.href;
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      {!state ? (
        <button onClick={reveal} disabled={busy} className="lux-btn lux-btn-secondary w-full text-[15px]" data-testid={`profile-contact-${method}-button`}>
          <Icon className="h-4 w-4" /> {busy ? "Opening..." : method === "phone" ? "Show number to call" : `Contact on ${METHOD_LABEL[method]}`}
        </button>
      ) : (
        <a href={state.href} target={state.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer nofollow"
          className="lux-btn lux-btn-secondary w-full text-[15px]" data-testid="profile-contact-revealed">
          <Icon className="h-4 w-4" /> {METHOD_LABEL[method]}: {state.display}
        </a>
      )}
      {err && <p className="mt-2 text-sm text-lux-error" role="alert">{err}</p>}
    </div>
  );
}

function ContactPanel({ p }) {
  const [waOpen, setWaOpen] = useState(false);
  const c = p.contact;
  if (!c.available) {
    return <p className="rounded-[10px] border border-lux-border p-4 text-sm text-lux-muted" data-testid="profile-contact-updating">This advertiser is updating their contact details. Please check back later.</p>;
  }
  return (
    <div className="space-y-3" data-testid="profile-contact-panel">
      {c.whatsapp ? (
        <>
          <button onClick={() => setWaOpen(true)} className="lux-btn lux-btn-primary w-full text-[15px]" data-testid="profile-contact-whatsapp-button">
            <WhatsAppIcon className="h-[18px] w-[18px]" /> WhatsApp {p.display_name}
          </button>
          <WhatsAppDialog open={waOpen} onOpenChange={setWaOpen} slug={p.slug} name={p.display_name} intro={c.intro} />
          {c.method !== "whatsapp" && <SecondaryContact slug={p.slug} method={c.method} />}
          <p className="text-center text-xs text-lux-muted">Opens WhatsApp{c.method === "phone" ? " or your phone app" : c.method === "signal" ? " or Signal" : ""} on your device. LUX has no in-app messaging.</p>
        </>
      ) : (
        <>
          <SecondaryContact slug={p.slug} method={c.method} />
          <p className="text-center text-xs text-lux-muted" data-testid="profile-contact-no-whatsapp">This advertiser publishes {METHOD_LABEL[c.method]} only. LUX has no in-app messaging.</p>
        </>
      )}
    </div>
  );
}

/** Share a profile: per-profile card (logo, name, area, verified badge - never photos) + a link that shows it in WhatsApp/social previews. */
function ShareDialog({ p }) {
  const [open, setOpen] = useState(false);
  const share = p.share || {};
  const canNative = typeof navigator !== "undefined" && !!navigator.share;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(share.share_url);
      toast.success("Share link copied", { description: "Pasting it in WhatsApp or social apps shows this card, not photos." });
    } catch (e) {
      toast.error("Couldn't copy the link");
    }
  };
  const native = async () => {
    try {
      await navigator.share({ title: `${p.display_name} on LUX`, text: `${p.display_name} - ${p.primary_area?.name}, ${p.city?.name}. Verified independent advertiser on LUX.`, url: share.share_url });
    } catch (e) {
      /* cancelled */
    }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="lux-btn lux-btn-secondary lux-btn-sm mt-1 shrink-0" data-testid="profile-share-button" aria-label={`Share ${p.display_name}'s profile`}>
        <Share2 className="h-4 w-4" aria-hidden="true" /> Share
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-lux-border bg-lux-surface text-lux-text sm:max-w-lg" data-testid="share-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="font-serif text-2xl font-medium">Share {p.display_name}'s profile</DialogTitle>
            <DialogDescription className="text-lux-muted">Previews show this card - name, area and verification only. Photos are never included.</DialogDescription>
          </DialogHeader>
          <div className="overflow-hidden rounded-[10px] border border-lux-border bg-lux-bg">
            <img src={share.card_url} alt={`Share card for ${p.display_name}: LUX logo, name, ${p.primary_area?.name}, verified badge`} className="aspect-[1200/630] w-full object-cover" loading="lazy" data-testid="share-card-preview" />
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {canNative && <button onClick={native} className="lux-btn lux-btn-primary w-full" data-testid="share-native-button"><Share2 className="h-4 w-4" /> Share...</button>}
            <button onClick={copy} className={`lux-btn ${canNative ? "lux-btn-secondary" : "lux-btn-primary"} w-full`} data-testid="share-copy-link-button"><Copy className="h-4 w-4" /> Copy share link</button>
            <a href={share.card_url} download={`lux-${p.slug}-card.png`} className="lux-btn lux-btn-ghost w-full" data-testid="share-download-card-link"><Download className="h-4 w-4" /> Download card image</a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TrustPanel({ p }) {
  const t = p.trust || {};
  const rows = [
    { icon: BadgeCheck, label: `Identity and age verified ${t.verified_days_ago === 0 ? "today" : `${t.verified_days_ago} days ago`}`, sub: t.reverify_due_in_days != null ? `Next selfie re-check due in ${t.reverify_due_in_days} days` : null },
    { icon: Images, label: `${t.photos_reviewed} photo${t.photos_reviewed === 1 ? "" : "s"} reviewed by a moderator`, sub: "No explicit images, no watermarks, no stolen photos" },
    { icon: UserRound, label: "One verified person, one profile", sub: "Independent advertiser - not an agency" },
    { icon: CalendarCheck, label: t.listed_since ? `Listed since ${fmtDate(t.listed_since)}` : "Newly listed", sub: t.last_approved_at ? `Last change approved ${fmtDate(t.last_approved_at)}` : null },
    { icon: Scale, label: "No paid placement", sub: "Position depends only on availability and verification freshness" },
  ];
  return (
    <section className="rounded-[12px] border border-lux-border bg-lux-surface p-5" aria-labelledby="trust-h" data-testid="profile-trust-panel">
      <p className="lux-label">Trust panel</p>
      <h2 id="trust-h" className="mt-2 font-serif text-2xl">What LUX has checked</h2>
      <ul className="mt-4 space-y-3">
        {rows.map(({ icon: Icon, label, sub }) => (
          <li key={label} className="flex gap-3">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" aria-hidden="true" />
            <div>
              <p className="text-sm text-lux-text">{label}</p>
              {sub && <p className="text-xs text-lux-muted">{sub}</p>}
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-lux-muted">LUX publishes no reviews or ratings. Facts only.</p>
    </section>
  );
}

export default function ProfilePage() {
  const { slug } = useParams();
  const { data, isLoading, error } = useQuery({ queryKey: ["profile", slug], queryFn: async () => (await api.get(`/public/profiles/${slug}`)).data,
    retry: (count, err) => ![404, 410].includes(err?.response?.status) && count < 2 });
  const status = error?.response?.status;
  useSeo(data?.seo ? { ...data.seo, image: data.share?.card_url } : { title: status === 410 ? "Profile no longer listed | LUX" : status ? "Not found | LUX" : "LUX", robots: status ? "noindex,follow" : undefined });
  useEffect(() => {
    if (data?.slug) api.post(`/public/profiles/${slug}/view`).catch(() => {});
  }, [data?.slug, slug]);

  if (status === 410) {
    return (
      <div className="mx-auto max-w-xl px-4 py-28 text-center" data-testid="profile-gone">
        <Clock className="mx-auto h-8 w-8 text-lux-silver" />
        <h1 className="mt-5 font-serif text-4xl">This profile is no longer listed</h1>
        <p className="mt-3 text-lux-muted">The advertiser's listing has expired.</p>
        <Link to="/london" className="lux-btn lux-btn-secondary mt-8">Browse London</Link>
      </div>
    );
  }
  if (status) {
    return (
      <div className="mx-auto max-w-xl px-4 py-28 text-center" data-testid="profile-not-found">
        <h1 className="font-serif text-4xl">Profile not found</h1>
        <p className="mt-3 text-lux-muted">This profile isn't available.</p>
        <Link to="/london" className="lux-btn lux-btn-secondary mt-8">Browse London</Link>
      </div>
    );
  }
  if (isLoading || !data) {
    return <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 lg:grid-cols-12"><div className="aspect-[3/4] animate-pulse rounded-[12px] bg-lux-surface lg:col-span-7" /><div className="h-96 animate-pulse rounded-[12px] bg-lux-surface lg:col-span-5" /></div>;
  }
  const p = data;
  const city = p.city || { slug: "london", name: "London" };
  return (
    <div className="mx-auto max-w-6xl px-4 pb-6 pt-6 sm:px-6 sm:pt-10 lg:px-8">
      <nav className="mb-6 text-xs text-lux-muted" aria-label="Breadcrumb">
        <Link to={`/${city.slug}`} className="hover:text-lux-silverBright" data-testid="profile-breadcrumb-city">{city.name}</Link> <span className="mx-1.5">/</span>
        <Link to={`/${city.slug}/${p.primary_area.slug}`} className="hover:text-lux-silverBright">{p.primary_area.name}</Link> <span className="mx-1.5">/</span>
        <span className="text-lux-text">{p.display_name}</span>
      </nav>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <div className="min-w-0 lg:col-span-7"><Gallery photos={p.photos} name={p.display_name} demo={p.is_demo} /></div>
        <div className="min-w-0 lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="flex flex-wrap items-center gap-2">
              {p.is_demo && <DemoTag testId="profile-demo-tag" />}
              <VerifiedBadge days={p.verified_days_ago} testId="profile-verified-badge" />
              {p.available_today && <AvailableBadge testId="profile-available-badge" />}
            </div>
            <div className="mt-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
              <h1 className="min-w-0 break-words font-serif text-5xl leading-none sm:text-6xl" data-testid="profile-name">{p.display_name}</h1>
              <div className="flex shrink-0 gap-2">
                <ShareDialog p={p} />
                <SaveButton slug={p.slug} name={p.display_name} size="button" className="lux-btn-sm mt-1 shrink-0" testId="profile-save-button" />
              </div>
            </div>
            <p className="mt-3 text-lux-silver" data-testid="profile-age-area">
              {p.age} · {GENDER_LABEL[p.gender]}{p.ethnicity?.label ? <> · <span data-testid="profile-ethnicity">{p.ethnicity.label}</span></> : null} · {p.service_types.map((s) => SERVICE_LABEL[s]).join(" & ")}
            </p>
            {fmtPrice(p.price) && (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-lux-border bg-lux-surface2 px-3 py-1.5 text-sm text-lux-silverBright" data-testid="profile-price" title="Rate for time and companionship, set by the advertiser">
                <Banknote className="h-3.5 w-3.5 text-lux-silver" aria-hidden="true" /> {fmtPrice(p.price)}
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <Link to={`/${city.slug}/${p.primary_area.slug}`} className="lux-chip py-1.5 hover:border-lux-silver/60" data-testid="profile-primary-area-chip"><MapPin className="h-3 w-3 text-lux-silver" /> {p.primary_area.name}, {city.name}</Link>
              {p.nearby_areas.map((a) => <Link key={a.slug} to={`/${city.slug}/${a.slug}`} className="lux-chip py-1.5 text-lux-muted hover:border-lux-silver/60">also {a.name}</Link>)}
            </div>
            <div className="my-7 h-px bg-lux-border" />
            <p className="font-serif text-2xl italic leading-snug text-lux-silverBright" data-testid="profile-headline">“{p.headline}”</p>
            <p className="mt-5 whitespace-pre-line text-[15px] leading-[1.8] text-lux-text/85" data-testid="profile-description">{p.description}</p>
            {p.stories?.length > 0 && <div className="mt-6"><StoriesRail groups={p.stories} title="Story today" /></div>}
            <div className="mt-8"><ContactPanel p={p} /></div>
            <div className="mt-6"><TrustPanel p={p} /></div>
            <div className="mt-6 flex gap-3 rounded-[10px] border border-lux-border bg-lux-surface2 p-4" data-testid="profile-safety-note">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" aria-hidden="true" />
              <p className="text-[13px] leading-relaxed text-lux-muted">
                <span className="text-lux-text">Never pay deposits to unknown accounts.</span> LUX never takes payments or bookings. <Link to="/safety" className="lux-link">Safety guide</Link>
              </p>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-lux-muted">Independent advertiser · one verified person</p>
              <ReportDialog profileSlug={p.slug} title={`Report ${p.display_name}'s profile`}
                trigger={<button className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-lux-silver hover:text-lux-silverBright" data-testid="profile-report-button"><Flag className="h-3.5 w-3.5" /> Report</button>} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
